import { CollapsibleList, SimpleListItem } from "@rmwc/list";
import '@rmwc/list/styles';
import { MODES, SERVICES_TYPES } from "Achat/Constants";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { selectIcon } from "Shared/Utils/Icon";
import "./AchatList.css";
import { AchatListItem } from "./AchatListItem";
import { useGetServiceNameLibelle } from "./Utils/achatHook";

// #region FONCTION COMPOSANT
/**
* Composant permettant de
* @class
* @category AchatList
*/
function AchatList(props) {
  // #region INITIALISATION
  const {
    categorie,
    categorieIndex,
    collapseByType,
    mode,
    onChange,
    openInfosTranches,
    parentSelected,
    setMode,
  } = props;

  // Initialisation des hooks pour récupérer les libellés
  const categorieLibelle = useGetServiceNameLibelle(categorie.name);
  //AYB 2026.01.12 Affichage de la description du service parent en priorité, avec fallback sur le système de traduction actuel
  const categorieParentLibelleHook = useGetServiceNameLibelle(parentSelected ? parentSelected.nom : null);
  const categorieParentLibelle = parentSelected?.description || categorieParentLibelleHook;

  const [categorieLib, setCategorieLib] = useState("");
  // #endregion

  // #region UTILS
  // Renvoie le titre de la catégorie en fonction du mode d'affichage de l'écran
  const getCategorieName = useCallback(() => {

    switch (mode) {
      case MODES.ENFANT:
        if (parentSelected) {
          return categorieParentLibelle;
        }
        break;

      case MODES.PARENT:
      case MODES.TOTAL:
      default:
        return categorieLibelle;
    }
  }, [mode, parentSelected, categorieParentLibelle, categorieLibelle]);

  // Renvoie si un service enfant est sélectionné
  const hasChildSelected = (serviceParent) => {
    let result = false;

    // Pas d'enfants donc toujours faux
    if (serviceParent.idServiceEnfants.length === 0) {
      return result;
    }

    serviceParent.idServiceEnfants.forEach(element => {
      const isChildSelected = categorie.services.find(serviceEnfant => serviceEnfant.idService === element && serviceEnfant.selected)

      if (isChildSelected) {
        result = true;
      }
    });

    return result;
  }

  // Renvoie si le service doit être affiché
  const isServiceVisible = (service) => {
    switch (mode) {
      case MODES.PARENT:
        return !service.idServiceParent;

      case MODES.ENFANT:
        // AYB 2026.02.18 si parent actif (rachat) mais non sélectionné : ne pas afficher la taxe, afficher les autres enfants
        if (!service.idServiceParent) return false;
        if (parentSelected?.rachat && !parentSelected?.selected && service.idServiceType === SERVICES_TYPES.TAXE_SEJOUR) return false;
        return true;

      case MODES.TOTAL:
        return service.selected;
      default:
        break;
    }
  }
  // #endregion

  // #region EVENTS
  // Modification d'une catégorie
  const handleChange = (serviceParam, index) => {
    let d = categorie;

    if (!d.services && !d.services[index]) {
      return;
    }
    d.services[index] = serviceParam;

    // Vérification si service de stationnement acheté et présence de taxes
    let haveParking = false;
    let haveTax = false;

    d.services.forEach(service => {
      if ((service.idServiceType === SERVICES_TYPES.PARKING) && service.selected) {
        haveParking = true;
      }

      if ((service.idServiceType === SERVICES_TYPES.TAXE_SEJOUR) && service.selected) {
        haveTax = true;
      }

      // Si service a un parent sans quantité, on vide la quantité du service enfant
      if (service.idServiceParent) {
        const serviceParent = d.services.find(item => item.idService === service.idServiceParent);

        if (!serviceParent.montant && !serviceParent.rachat) {
          service.montant = 0;
          service.quantite = 0;
          service.selected = false;
        }
      }
    });

    // Mise à jour des données
    onChange(d, categorieIndex, haveParking, haveTax);
  }
  // #endregion

  // #region HOOK D'EFFET
  useEffect(() => {
    setCategorieLib(getCategorieName())
  }, [mode, getCategorieName]);
  // #endregion

  // #region INTERFACE
  return (
    <CollapsibleList
      key={`${categorie.id}-${collapseByType ? "closed" : "open"}`}
      defaultOpen={!collapseByType}
      handle={
        <SimpleListItem
          className="Achat-HeaderList"
          graphic={selectIcon(categorie.id)}
          metaIcon="chevron_right"
          text={categorieLib}
        />
      }
    >
      {
        categorie.services.map((item, index) => {
          // On vérifie si un service dans cette catégorie est déjà sélectionné
          const serviceInCategorieSelected = categorie.services.find((service) => service.idService !== item.idService && (service.selected || service.rachat));
          // On recherche si le parent est sélectionné
          const isParentSelected = item.serviceParent ? categorie.services.find((service) => service.idService === item.serviceParent.idService && (service.selected || service.rachat)) : null
          // on recherche si au moins un des services enfant est sélectionné
          const isChildSelected = hasChildSelected(item);

          return isServiceVisible(item) ? (
            <AchatListItem
              serviceInCategorieSelected={serviceInCategorieSelected}
              isChildSelected={isChildSelected}
              key={item.idService}
              mode={mode}
              onChange={handleChange}
              openInfosTranches={openInfosTranches}
              parentSelected={isParentSelected}
              service={item}
              serviceIndex={index}
              setMode={setMode}
            />
          )
            :
            null
        })
      }
    </ CollapsibleList>
  );
  // #endregion
};
// #endregion

// #region PROPRIETES
/**
* Type des propriétés de {@link AchatList}
  * @typedef {Object} AchatList.propTypes
  * @property {object} categorie Informations de la catégorie
  * @property {number} categorieIndex Index de la catégorie
  * @property {number} mode mode de l'écran
 */

AchatList.propTypes = {
  categorie: PropTypes.object.isRequired,
  categorieIndex: PropTypes.number.isRequired,
  collapseByType: PropTypes.bool,
  number: PropTypes.object.isRequired,
};

AchatList.defaultProps = {
  collapseByType: false,
};
// #endregion

export { AchatList };

